import { useState, useCallback } from "react";
import {
  CheckCircle2,
  Circle,
  Lock,
  Clock,
  Target,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import Badge from "../components/Badge";
import LoadingSkeleton from "../components/LoadingSkeleton";
import ActivityModal from "../components/ActivityModal";
import Celebration from "../components/Celebration";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "../lib/useQuery";
import { getStudent, getActivities, updateActivity } from "../lib/queries";
import imgPuntoPartida from "../assets/images/portada-tu-punto-de-partida.jpg";
import imgPolaris from "../assets/images/portada-polaris.jpg";
import imgNucleo from "../assets/images/portada-nucleo.png";
import imgVentas from "../assets/images/portada-ventas.png";
import imgLibera from "../assets/images/portada-libera-tu-tiempo.png";

const BLOCKS = [
  { order: 0, name: "Tu punto de partida", weeks: 2, color: "slate", image: imgPuntoPartida },
  { order: 1, name: "Polaris", weeks: 3, color: "cyan", image: imgPolaris },
  { order: 2, name: "Núcleo", weeks: 3, color: "sky", image: imgNucleo },
  { order: 3, name: "Sistema de Fidelización PRO", weeks: 2, color: "blue", image: imgVentas },
  { order: 4, name: "Libera tu tiempo", weeks: 2, color: "emerald", image: imgLibera },
];

const BLOCK_STYLES = {
  slate:  { border: "border-l-slate-400",  bg: "bg-slate-50/40",  dot: "bg-slate-400",  text: "text-slate-600" },
  teal:   { border: "border-l-teal-400",   bg: "bg-teal-50/40",   dot: "bg-teal-400",   text: "text-teal-700" },
  cyan:   { border: "border-l-cyan-400",   bg: "bg-cyan-50/40",   dot: "bg-cyan-400",   text: "text-cyan-700" },
  sky:    { border: "border-l-sky-400",    bg: "bg-sky-50/40",    dot: "bg-sky-400",    text: "text-sky-700" },
  blue:   { border: "border-l-blue-400",   bg: "bg-blue-50/40",   dot: "bg-blue-400",   text: "text-blue-700" },
  emerald:{ border: "border-l-emerald-400",bg: "bg-emerald-50/40",dot: "bg-emerald-400",text: "text-emerald-700" },
};

const TIMELINE_BLOCKS = BLOCKS.filter((b) => b.weeks);
const TOTAL_WEEKS = TIMELINE_BLOCKS.reduce((s, b) => s + b.weeks, 0);

function getWeeksElapsed(startDate) {
  if (!startDate) return 0;
  const diff = Date.now() - new Date(startDate).getTime();
  return Math.max(0, diff / (7 * 24 * 60 * 60 * 1000));
}

function groupByBlock(activities) {
  const map = new Map();

  for (const a of activities ?? []) {
    const key = a.block_order ?? 1;
    if (!map.has(key)) {
      map.set(key, { order: key, name: a.block_name, items: [] });
    }
    map.get(key).items.push(a);
  }

  for (const block of BLOCKS) {
    if (!map.has(block.order)) {
      map.set(block.order, {
        order: block.order,
        name: block.name,
        items: [],
      });
    }
  }

  return [...map.values()].sort((a, b) => a.order - b.order);
}

function isBlockUnlocked(blockOrder, allActivities) {
  if (blockOrder === 0) return true;
  const prev = (allActivities ?? []).filter(
    (a) => a.block_order === blockOrder - 1
  );
  if (prev.length === 0) return false;
  return prev.every((a) => a.completed);
}

function isBlockComplete(blockOrder, allActivities) {
  const items = (allActivities ?? []).filter(
    (a) => a.block_order === blockOrder
  );
  return items.length > 0 && items.every((a) => a.completed);
}

function isTaskUnlocked(task, blockItems, blockUnlocked) {
  if (!blockUnlocked) return false;
  if (task.completed) return true;
  const sorted = [...blockItems].sort((a, b) => a.sort_order - b.sort_order);
  const idx = sorted.findIndex((i) => i.id === task.id);
  if (idx <= 0) return true;
  return sorted[idx - 1].completed;
}

function scrollToBlock(order) {
  const el = document.getElementById(`block-${order}`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function ProgramTimeline({ startDate, activities }) {
  const weeksElapsed = getWeeksElapsed(startDate);
  const weeksLeft = Math.max(0, Math.ceil(TOTAL_WEEKS - weeksElapsed));
  const timePct = Math.min(100, Math.round((weeksElapsed / TOTAL_WEEKS) * 100));

  const completed = activities?.filter((a) => a.completed).length ?? 0;
  const total = activities?.length || 1;
  const actPct = Math.round((completed / total) * 100);

  let accWeeks = 0;
  let currentBlockOrder = TIMELINE_BLOCKS[TIMELINE_BLOCKS.length - 1].order;
  for (const b of TIMELINE_BLOCKS) {
    accWeeks += b.weeks;
    if (weeksElapsed < accWeeks) {
      currentBlockOrder = b.order;
      break;
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-brand-light flex flex-col items-center justify-center">
            <span className="text-lg font-extrabold text-brand leading-none">
              {weeksLeft}
            </span>
            <span className="text-[9px] font-semibold text-brand/70 uppercase">
              sem
            </span>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Semana {Math.min(Math.ceil(weeksElapsed) || 1, TOTAL_WEEKS)} de {TOTAL_WEEKS}
            </p>
            <p className="text-xs text-gray-500">
              Deberías estar en <span className="font-semibold text-brand">{TIMELINE_BLOCKS.find((b) => b.order === currentBlockOrder)?.name}</span>
            </p>
          </div>
        </div>

        <div className="sm:ml-auto flex items-center gap-2 sm:w-32">
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand to-brand-dark rounded-full transition-all"
              style={{ width: `${timePct}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-gray-500 w-8 text-right">
            {timePct}%
          </span>
        </div>
      </div>

      {/* Mobile: compact */}
      <div className="grid grid-cols-5 divide-x divide-gray-100 sm:hidden">
        {TIMELINE_BLOCKS.map((b) => {
          const done = isBlockComplete(b.order, activities);
          const active = b.order === currentBlockOrder;
          return (
            <div
              key={b.order}
              onClick={() => scrollToBlock(b.order)}
              className={`py-3 px-1.5 text-center transition-colors cursor-pointer ${
                active ? "bg-brand-light/40" : done ? "bg-emerald-50/50" : ""
              }`}
            >
              {done ? (
                <CheckCircle2 size={16} className="text-emerald-500 mx-auto mb-1" />
              ) : (
                <div className={`w-3.5 h-3.5 rounded-full mx-auto mb-1 ${
                  active ? "bg-brand ring-2 ring-brand/30 ring-offset-1" : "bg-gray-200"
                }`} />
              )}
              <p className={`text-[10px] font-medium leading-tight ${
                active ? "text-brand font-bold" : done ? "text-emerald-600" : "text-gray-400"
              }`}>{b.name}</p>
              <p className="text-[9px] text-gray-400 mt-0.5">{b.weeks} sem.</p>
            </div>
          );
        })}
      </div>

      {/* Desktop: image cards */}
      <div className="hidden sm:grid grid-cols-5 gap-2.5 p-3">
        {TIMELINE_BLOCKS.map((b) => {
          const done = isBlockComplete(b.order, activities);
          const active = b.order === currentBlockOrder;
          return (
            <div key={b.order} onClick={() => scrollToBlock(b.order)} className="cursor-pointer">
              {b.image ? (
                <div className={`relative rounded-lg overflow-hidden border-2 transition-transform hover:scale-[1.03] ${
                  active
                    ? "border-brand ring-2 ring-brand/30"
                    : done
                      ? "border-emerald-400"
                      : "border-gray-200"
                }`}>
                  <img
                    src={b.image}
                    alt={b.name}
                    className={`w-full aspect-video object-cover ${
                      !active && !done ? "grayscale opacity-50" : ""
                    }`}
                  />
                  {done && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center ring-2 ring-white">
                      <CheckCircle2 size={12} className="text-white" />
                    </div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                    <p className="text-[11px] font-semibold leading-tight text-white drop-shadow-sm">
                      {b.name}
                    </p>
                    <p className="text-[9px] text-white/70">{b.weeks} sem.</p>
                  </div>
                </div>
              ) : (
                <div className={`flex flex-col items-center justify-center aspect-video rounded-lg border-2 ${
                  active ? "border-brand bg-brand-light/40" : done ? "border-emerald-400 bg-emerald-50" : "border-gray-200 bg-gray-50"
                }`}>
                  {done ? (
                    <CheckCircle2 size={16} className="text-emerald-500 mb-1" />
                  ) : (
                    <div className={`w-3 h-3 rounded-full mb-1 ${active ? "bg-brand" : "bg-gray-300"}`} />
                  )}
                  <p className={`text-[11px] font-medium leading-tight ${
                    active ? "text-brand" : done ? "text-emerald-600" : "text-gray-400"
                  }`}>{b.name}</p>
                  <p className="text-[9px] text-gray-400">{b.weeks} sem.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-4 md:px-5 py-3 border-t border-gray-100 flex items-center gap-3">
        <div className="h-10 px-3 rounded-xl bg-emerald-50 flex items-center justify-center">
          <span className="text-base font-extrabold text-emerald-600">
            {actPct}%
          </span>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {completed}/{total} actividades
          </p>
        </div>
        <div className="flex-1 max-w-xs">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${actPct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

function TaskRow({ task, unlocked, onClick }) {
  const isLocked = !unlocked;
  const canClick = unlocked;
  const isAdminPending = task.admin_only && !task.completed;

  return (
    <button
      onClick={canClick ? onClick : undefined}
      disabled={!canClick}
      className={`w-full flex items-center gap-3 px-4 md:px-6 py-3.5 text-left transition-colors ${
        isLocked
          ? "opacity-40 bg-gray-50/50 cursor-default"
          : task.completed
            ? "hover:bg-gray-50 cursor-pointer"
            : "hover:bg-brand-light/40 cursor-pointer active:bg-brand-light/60"
      }`}
    >
      {isLocked ? (
        <Lock size={15} className="text-gray-300 shrink-0" />
      ) : task.completed ? (
        <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
      ) : (
        <Circle size={18} className="text-brand shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <span
          className={`text-sm block ${
            task.completed
              ? "text-gray-500"
              : isLocked
                ? "text-gray-400"
                : "text-gray-800 font-medium"
          }`}
        >
          {task.name}
        </span>
        {task.completed && task.completed_at && (
          <span className="text-[11px] text-gray-400 mt-0.5 block">
            Completada el {shortDate(task.completed_at)}
          </span>
        )}
      </div>

      {!isLocked && (
        isAdminPending ? (
          <span className="shrink-0 flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
            <Clock size={12} />
            Pendiente de validación por ETC
          </span>
        ) : (
          <span
            className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
              task.completed
                ? "bg-emerald-50 text-emerald-600"
                : "bg-orange-50 text-orange-500"
            }`}
          >
            {task.completed ? "Hecho" : "Pendiente"}
          </span>
        )
      )}
    </button>
  );
}

function BlockSection({
  block,
  blockMeta,
  unlocked,
  allActivities,
  defaultOpen,
  onTaskClick,
  colorKey,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const completed = block.items.filter((a) => a.completed).length;
  const total = block.items.length;
  const allDone = total > 0 && completed === total;
  const style = BLOCK_STYLES[colorKey] || BLOCK_STYLES.sky;

  return (
    <div
      id={`block-${block.order}`}
      className={`border rounded-xl overflow-hidden transition-colors border-l-4 ${style.border} ${
        unlocked ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50/50"
      }`}
    >
      <button
        onClick={() => unlocked && total > 0 && setOpen(!open)}
        className={`w-full flex items-center gap-3 px-4 md:px-5 py-3.5 transition-colors text-left ${
          unlocked && total > 0 ? "hover:bg-gray-50" : "cursor-default"
        }`}
      >
        {!unlocked ? (
          <Lock size={15} className="text-gray-300 shrink-0" />
        ) : open ? (
          <ChevronDown size={16} className="text-gray-400 shrink-0" />
        ) : (
          <ChevronRight size={16} className="text-gray-400 shrink-0" />
        )}
        <div className={`w-2 h-2 rounded-full shrink-0 ${unlocked ? style.dot : "bg-gray-300"}`} />
        <span
          className={`flex-1 text-sm font-semibold ${
            unlocked ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {block.name}
          {blockMeta && (
            <span className="font-normal text-gray-400 ml-1.5 text-xs">
              · {blockMeta.weeks ? `${blockMeta.weeks} sem.` : blockMeta.subtitle}
            </span>
          )}
        </span>
        {unlocked && total > 0 && (
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              allDone
                ? "bg-emerald-50 text-emerald-600"
                : `${style.bg} ${style.text}`
            }`}
          >
            {completed}/{total}
          </span>
        )}
        {!unlocked && (
          <span className="text-xs text-gray-400 font-medium">
            Bloqueado
          </span>
        )}
      </button>

      {open && unlocked && block.items.length > 0 && (
        <div className="divide-y divide-gray-100 border-t border-gray-100">
          {block.items.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              unlocked={isTaskUnlocked(task, block.items, unlocked)}
              onClick={() => onTaskClick(task)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Activities() {
  const { user } = useAuth();
  const { refetchStudent } = useOutletContext();
  const { data: student, loading: l1 } = useQuery(
    () => getStudent(user.id),
    [user.id]
  );
  const {
    data: activities,
    loading: l2,
    refetch,
  } = useQuery(() => getActivities(user.id), [user.id]);

  const [selectedTask, setSelectedTask] = useState(null);
  const [celebration, setCelebration] = useState(null);

  const handleComplete = useCallback(
    async (templateId, updates) => {
      const wasCompletedBefore = activities?.find((a) => a.id === templateId)?.completed;
      const isBeingCompleted = updates.completed === true;
      const justCompleted = isBeingCompleted && !wasCompletedBefore;

      await updateActivity(user.id, templateId, updates);
      await refetch();
      refetchStudent();

      if (!justCompleted) return;

      const updatedActivities = await getActivities(user.id);
      const completedTask = updatedActivities.find((a) => a.id === templateId);
      if (!completedTask) return;

      const blockTasks = updatedActivities.filter(
        (a) => a.block_order === completedTask.block_order
      );
      const completedInBlock = blockTasks.filter((a) => a.completed).length;
      const totalInBlock = blockTasks.length;
      const pendingInBlock = totalInBlock - completedInBlock;

      const totalCompleted = updatedActivities.filter((a) => a.completed).length;
      const totalTasks = updatedActivities.length;

      if (totalCompleted === totalTasks) {
        setCelebration("program_complete");
      } else if (pendingInBlock === 0) {
        setCelebration("block_complete");
      }
    },
    [refetch, refetchStudent, user.id, activities]
  );

  if (l1 || l2) return <LoadingSkeleton />;

  const blocks = groupByBlock(activities);

  return (
    <div className="animate-fade-in space-y-5">
      <ProgramTimeline
        startDate={student?.start_date}
        activities={activities}
      />

      <div className="space-y-3">
        {blocks.map((block) => {
          const meta = BLOCKS.find((b) => b.order === block.order);
          const unlocked = isBlockUnlocked(block.order, activities);

          return (
            <BlockSection
              key={block.order}
              block={block}
              blockMeta={meta}
              unlocked={unlocked}
              allActivities={activities}
              defaultOpen={
                unlocked && !block.items.every((a) => a.completed)
              }
              onTaskClick={setSelectedTask}
              colorKey={meta?.color}
            />
          );
        })}
      </div>

      {selectedTask && (
        <ActivityModal
          task={selectedTask}
          onComplete={handleComplete}
          onClose={() => setSelectedTask(null)}
        />
      )}

      {celebration && (
        <Celebration
          type={celebration}
          onComplete={() => setCelebration(null)}
        />
      )}
    </div>
  );
}
